import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import ProfileConfirmCard from '../components/onboarding/ProfileConfirmCard'
import ManualOnboardingForm from '../components/onboarding/ManualOnboardingForm'
import BaselineStep from '../components/onboarding/BaselineStep'

// Read a File as bare base64 (no data: prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(',')[1])
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

function Bubble({ role, children }) {
  const mine = role === 'user'
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '84%',
          padding: '11px 14px',
          borderRadius: 16,
          borderBottomRightRadius: mine ? 4 : 16,
          borderBottomLeftRadius: mine ? 16 : 4,
          background: mine ? 'var(--teal)' : 'var(--surface)',
          color: mine ? '#fff' : 'var(--text)',
          border: mine ? 'none' : '1px solid var(--border)',
          fontSize: 15,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default function OnboardingPage() {
  const { user, signOut } = useAuth()
  const { reload } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [messages, setMessages] = useState([]) // {role, text, attachment?}
  const [input, setInput] = useState('')
  const [pendingDoc, setPendingDoc] = useState(null) // {name, media_type, data}
  const [thinking, setThinking] = useState(false)
  const [profile, setProfile] = useState(null)
  const [manual, setManual] = useState(false)
  const [saving, setSaving] = useState(false)
  const [baselineFor, setBaselineFor] = useState(null) // { id, name } after confirm
  const [addAnother, setAddAnother] = useState(null) // name of the just-finished recipient
  const scrollRef = useRef(null)
  const fileRef = useRef(null)
  const started = useRef(false)

  // Kick off Ginie's opening line once.
  useEffect(() => {
    if (started.current) return
    started.current = true
    callGinie([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, thinking])

  async function callGinie(history, doc) {
    setThinking(true)
    try {
      const { data, error } = await supabase.functions.invoke('ginie', {
        body: {
          messages: history.map((m) => ({ role: m.role, text: m.text })),
          document: doc ? { media_type: doc.media_type, data: doc.data } : undefined,
        },
      })
      if (error) throw error
      if (data?.status === 'ready') {
        setProfile(data.profile)
      } else if (data?.reply) {
        setMessages((m) => [...m, { role: 'assistant', text: data.reply }])
      } else {
        throw new Error(data?.error || 'Ginie had trouble responding.')
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: "I'm having trouble connecting right now. You can try again, or set things up manually below.",
        },
      ])
      // eslint-disable-next-line no-console
      console.warn('Ginie error:', err)
    } finally {
      setThinking(false)
    }
  }

  async function send() {
    const text = input.trim()
    if ((!text && !pendingDoc) || thinking) return
    const userMsg = {
      role: 'user',
      text: text || `Please read this: ${pendingDoc?.name}`,
      attachment: pendingDoc?.name,
    }
    const next = [...messages, userMsg]
    setMessages(next)
    setInput('')
    const doc = pendingDoc
    setPendingDoc(null)
    await callGinie(next, doc)
  }

  async function onFile(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF for now.')
      return
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('That file is over 25MB.')
      return
    }
    try {
      const data = await fileToBase64(file)
      setPendingDoc({ name: file.name, media_type: file.type, data })
    } catch {
      toast.error("Couldn't read that file.")
    }
  }

  // Persist the confirmed profile to Supabase.
  async function saveProfile(p) {
    if (saving) return
    setSaving(true)
    try {
      const priorities = [p.care_goals, p.what_matters_daily && `What matters day to day: ${p.what_matters_daily}`]
        .filter(Boolean)
        .join('\n\n')

      // The schema has no dedicated risk_flags column; the dashboard surfaces
      // risk-type conditions from `conditions`, so merge the flags in (deduped).
      const conditions = [...(p.conditions || [])]
      for (const flag of p.risk_flags || []) {
        if (!conditions.some((c) => c.toLowerCase() === flag.toLowerCase())) {
          conditions.push(flag)
        }
      }

      const { data: recipient, error: rErr } = await supabase
        .from('care_recipients')
        .insert({
          created_by: user.id,
          full_name: p.full_name,
          preferred_name: p.preferred_name || null,
          date_of_birth: p.date_of_birth || null,
          conditions,
          allergies: p.allergies || [],
          emergency_contact_name: p.emergency_contact_name || null,
          emergency_contact_phone: p.emergency_contact_phone || null,
          family_doctor_name: p.family_doctor_name || null,
          family_doctor_phone: p.family_doctor_phone || null,
          care_stage: p.care_stage || 'unknown',
        })
        .select()
        .single()
      if (rErr) throw rErr
      const rid = recipient.id

      const nextReview = new Date()
      nextReview.setDate(nextReview.getDate() + 90)
      const writes = []
      if (priorities.trim()) {
        writes.push(
          supabase.from('care_plans').insert({
            care_recipient_id: rid,
            created_by: user.id,
            care_stage: p.care_stage || 'unknown',
            current_priorities: priorities,
            next_review_date: nextReview.toISOString().slice(0, 10),
            is_active: true,
          })
        )
      }
      if (p.medications?.length) {
        writes.push(
          supabase.from('medications').insert(
            p.medications
              .filter((m) => m.name?.trim())
              .map((m) => ({
                care_recipient_id: rid,
                name: m.name,
                dose: m.dose || null,
                timing: m.timing || null,
                purpose: m.purpose || null,
                prescribing_doctor: m.prescribing_doctor || null,
              }))
          )
        )
      }
      if (p.initial_tasks?.length) {
        writes.push(
          supabase.from('tasks').insert(
            p.initial_tasks
              .filter((t) => t.title?.trim())
              .map((t) => ({
                care_recipient_id: rid,
                created_by: user.id,
                title: t.title,
                description: t.description || null,
              }))
          )
        )
      }
      const results = await Promise.all(writes)
      const failed = results.find((r) => r.error)
      if (failed) throw failed.error

      // Profile saved — move on to the quick functional baseline (Step 2 of 2)
      // before entering the app.
      setBaselineFor({ id: rid, name: p.preferred_name || p.full_name })
      setSaving(false)
    } catch (err) {
      toast.error(humanizeError(err))
      setSaving(false)
    }
  }

  // Restart Ginie for an additional care recipient.
  function startAnother() {
    setAddAnother(null)
    setProfile(null)
    setBaselineFor(null)
    setMessages([])
    setInput('')
    setPendingDoc(null)
    callGinie([]) // fresh opening line
  }

  // ── Functional baseline (after confirm, before dashboard) ──
  if (baselineFor) {
    return (
      <BaselineStep
        recipientId={baselineFor.id}
        name={baselineFor.name}
        finishLabel="Continue →"
        onComplete={async () => {
          await reload()
          setAddAnother(baselineFor.name)
          setBaselineFor(null)
        }}
      />
    )
  }

  // ── Offer to set up another recipient (Mom + Dad, etc.) ──
  if (addAnother) {
    return (
      <div className="screen">
        <div className="container center" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
          <div style={{ fontSize: 52 }}>🎉</div>
          <h1 className="title">{addAnother} is all set</h1>
          <p className="subtitle" style={{ maxWidth: 340 }}>
            Their care profile and baseline are ready. Caring for more than one person?
          </p>
          <div className="stack" style={{ gap: 10, marginTop: 8, width: '100%' }}>
            <button className="btn btn-lg btn-block" onClick={startAnother}>+ Set up someone else</button>
            <button className="btn btn-ghost btn-block" onClick={() => navigate('/', { replace: true })}>Go to dashboard →</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Confirmation step ───────────────────────────────
  if (profile) {
    return (
      <ProfileConfirmCard
        profile={profile}
        saving={saving}
        onBack={() => setProfile(null)}
        onConfirm={saveProfile}
      />
    )
  }

  // ── Manual fallback ────────────────────────────────
  if (manual) {
    return <ManualOnboardingForm onBack={() => setManual(false)} />
  }

  // ── Chat ───────────────────────────────────────────
  return (
    <div className="screen" style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      <header
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}
      >
        <div className="container spread" style={{ height: 56 }}>
          <div className="row" style={{ gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 10, background: 'var(--teal)',
                color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              G
            </div>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontWeight: 700 }}>Ginie</div>
              <div className="muted" style={{ fontSize: 12 }}>Your care coordinator</div>
            </div>
          </div>
          <button
            onClick={() => signOut()}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13 }}
          >
            Sign out
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '16px' }}
      >
        <div className="container" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {m.attachment && (
                <div style={{ alignSelf: 'flex-end', fontSize: 12 }} className="badge">
                  📎 {m.attachment}
                </div>
              )}
              <Bubble role={m.role}>{m.text}</Bubble>
            </div>
          ))}
          {thinking && (
            <Bubble role="assistant">
              <span className="muted">Ginie is typing…</span>
            </Bubble>
          )}
          {messages.length <= 1 && !thinking && (
            <button
              onClick={() => setManual(true)}
              style={{ alignSelf: 'center', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, marginTop: 8 }}
            >
              Prefer a form? Set up manually
            </button>
          )}
        </div>
      </div>

      {/* Composer */}
      <div style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="container" style={{ padding: '10px 16px' }}>
          {pendingDoc && (
            <div className="row spread" style={{ marginBottom: 8 }}>
              <span className="badge">📎 {pendingDoc.name}</span>
              <button onClick={() => setPendingDoc(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)' }}>
                Remove
              </button>
            </div>
          )}
          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} style={{ display: 'none' }} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Attach a document"
              style={{
                minWidth: 46, height: 46, borderRadius: 12, border: '1.5px solid var(--border)',
                background: 'var(--surface)', fontSize: 20,
              }}
            >
              📎
            </button>
            <textarea
              className="textarea"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder="Message Ginie…"
              style={{ minHeight: 46, maxHeight: 120, resize: 'none', flex: 1 }}
            />
            <button
              type="button"
              onClick={send}
              disabled={thinking || (!input.trim() && !pendingDoc)}
              className="btn"
              style={{ minWidth: 46, height: 46, padding: 0, borderRadius: 12 }}
              aria-label="Send"
            >
              ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
